// Component Name:  Dropdown component
// Author: Lavanya S
// Platform:
// OS: Windows
// IDE: Visual Studio Code
// Reference: Original
// Date: 29-01-2025
// Description:  This is an reusable dropdown component using React
//               It allows users to select the value in the dropdown list
// Dependencies: React library
// Interfaces: Accepts props including options, onSelect, selectedOption, closeDropDown
// Maintenance History: 1.0 First Version

import React, { useState, useEffect, useRef } from "react";

/**
 * 
 * @param {Array of Objects} options: to display the dropdown options, Object must contain name and value
 * @param {function} onSelect: callback function that defines what happen on Click of the Option
 * @param {Object} selectedOption:  current selected options
 * @param {function} closeDropDown :  is function defines what should happen when user click outside the dropdown, usually used to close the dropdown the user clicks outside the dropdown

 */
const DropDown = ({ options, onSelect, selectedOption, closeDropDown = () => {} }) => {
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    setSelected(selectedOption);
  }, [selectedOption]);

  const [hoveredIndex, setHoveredIndex] = useState(null); // State to track hovered option
  const dropdownRef = useRef(null); // Ref to track the dropdown container

  // Handle option selection
  const handleSelect = (option) => {
    // setSelected(option);
    onSelect(option);
  };

  const handleHover = (index) => {
    setHoveredIndex(index);
  };

  // Close dropdown when click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropDown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const itemRefs = useRef([]);

  useEffect(() => {
    if (selectedOption && options.length && itemRefs.current.length > 0) {
      const selectedIndex = options.findIndex((opt) => opt.value === selectedOption.value);
      if (selectedIndex !== -1 && itemRefs.current[selectedIndex]) {
        itemRefs.current[selectedIndex].scrollIntoView({
          behavior: "auto",
          block: "start",
        });
      }
    }
  }, [selectedOption, options]);
  return (
    <>
      <style>
        {`
          .custom-dropdown::-webkit-scrollbar {
              width: 6px;
          }

          .custom-dropdown::-webkit-scrollbar-track {
              background: #EFEFEF;
          }

          .custom-dropdown::-webkit-scrollbar-thumb {
              background-color: #008CD7;
              border-radius: 6px;
          }

          .custom-dropdown {
              scrollbar-width: thin;            
              scrollbar-color: #008CD7 #EFEFEF;   
          }
        `}
      </style>
      <ul className="custom-dropdown" style={styles.dropdownList} ref={dropdownRef}>
        {options.map((option, index) => {
          const { name, value } = option;
          return (
            <li
              key={index}
              ref={(el) => (itemRefs.current[index] = el)}
              onClick={() => handleSelect(option)}
              onMouseEnter={() => handleHover(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{
                ...styles.option,
                backgroundColor: selected?.value === value ? "#005C8E" : hoveredIndex === index && "#A2A2A2",
                color: selected?.value === value ? "#FFFFFF" : hoveredIndex === index && "#EEEEEE",
              }}
            >
              {option?.image ? (
                <img
                  src={selected?.value === value ? option?.hoverImage : option?.image}
                  alt="image"
                  style={{ width: "16px", height: "16px", marginBottom: "4px", marginRight: "3px" }}
                />
              ) : (
                <i className={option?.icon} style={{ width: "16px", height: "16px", marginBottom: "4px", marginRight: "3px" }}></i>
              )}
              {name}
            </li>
          );
        })}
      </ul>
    </>
  );
};

const styles = {
  dropdownContainer: {
    // border: `1.5pt solid #008CD7`,
    position: "absolute",
    top: "120px",
    right: "126px",
    padding: "10px 0 10px",
    backgroundColor: "white",
    borderRadius: "4px",
    width: "200px",
  },
  container: {
    position: "relative",
    width: "210px",
  },
  button: {
    width: "100%",
    padding: "10px",
    fontSize: "16px",
    borderRadius: "4px",
    cursor: "pointer",
    border: "1px solid #ccc",
    textAlign: "left",
  },
  dropdownList: {
    margin: 0,
    padding: 0,
    listStyleType: "none",
    backgroundColor: "#C1C1C1",
    border: "1px solid #ccc",
    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
    zIndex: 10,
    border: `2px solid #008CD7`,
    borderLeft: "none",
    borderRight: "none",
    maxHeight: "350px",
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "thin", // For Firefox
    scrollbarColor: "#008CD7 #EFEFEF", // For Firefox
  },
  option: {
    padding: "10px",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
  },
};

export default DropDown;
